import { createProtectedLoader } from '@components/apollo';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Sparkles, Wrench, Link2, Heart } from 'lucide-react';
import Modal from '@components/modal';
import { KawaiiButton } from '@components/button';

const MCPSettings = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('Not connected');
  
  // Auth data that would come from your auth system
  const [authData, setAuthData] = useState({
    userToken: 'user-auth-token-123',
    sessionId: crypto.randomUUID().substring(0, 8),
    serverWsEndpoint: 'wss://api.yourserver.com/mcp-ws'
  });

  const connectToLocalMcp = () => {
    setIsConnecting(true);
    setConnectionStatus('Opening MCP app...');
    
    try {
      // Create URL with auth data to pass to local app
      const params = new URLSearchParams({
        userToken: authData.userToken,
        sessionId: authData.sessionId,
        serverEndpoint: authData.serverWsEndpoint
      });
      
      // This URL will trigger your local app to open
      // Format: myapp://data?userToken=X&sessionId=Y&serverEndpoint=Z
      const appUrl = `kawaiilab://data?${params.toString()}`;
      
      // Open the URL with the custom protocol handler
      window.location.href = appUrl;
      
      // Since we can't directly know when the app connects,
      // we'll set up a listener for server notifications via WebSocket
      checkConnectionStatus();
    } catch (error) {
      console.error('Failed to open MCP app:', error);
      setConnectionStatus('Failed to open MCP app');
      setIsConnecting(false);
    }
  };
  
  const checkConnectionStatus = () => {
    // In a real implementation, you would:
    // 1. Set up a WebSocket connection to your server
    // 2. Listen for connection status events from the server
    // 3. Update the UI based on those events
    
    // For demo purposes, using a timeout to simulate connection process
    setConnectionStatus('Waiting for MCP to connect...');
    
    // Simulating connection establishment
    setTimeout(() => {
      setIsConnected(true);
      setIsConnecting(false);
      setConnectionStatus('Connected to MCP');
    }, 2000);
  };

  const disconnectFromLocalMcp = () => {
    // In a real implementation:
    // 1. Send disconnect request to server
    // 2. Server would notify MCP app to disconnect
    // 3. Update UI based on disconnect confirmation
    
    setIsConnected(false);
    setConnectionStatus('Disconnected');
  };

  return (
    <div className="h-[calc(100vh-73px)] w-full flex flex-col items-center p-6 bg-linear-to-br from-pink-50 to-white dark:from-gray-900 dark:to-gray-800">
      <div className="max-w-3xl w-full">
        {/* Header with sparkles */}
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl font-bold text-pink-700 dark:text-pink-300 flex items-center gap-2">
            <Sparkles className="h-6 w-6" />
            MCP Connection
          </h1>
          <div className="bg-pink-100 dark:bg-pink-900 text-pink-600 dark:text-pink-300 rounded-full px-3 py-1 text-sm">
            Session: {authData.sessionId}
          </div>
        </div>

        {/* Main connection card */}
        <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-xs rounded-2xl shadow-lg p-6 mb-6 border border-pink-100 dark:border-pink-800">
          {/* Status display */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <div className={`h-4 w-4 rounded-full ${isConnected ? 'bg-green-500' : isConnecting ? 'bg-yellow-400' : 'bg-gray-400'}`}></div>
              <span className="text-gray-700 dark:text-gray-300 font-medium">
                {connectionStatus}
              </span>
            </div>
            
            {isConnected && (
              <span className="text-xs bg-pink-100 dark:bg-pink-900 text-pink-600 dark:text-pink-300 rounded-full px-2 py-1">
                MCP Active
              </span>
            )}
          </div>
          
          {/* Connection animation (when connecting) */}
          {isConnecting && (
            <div className="flex justify-center my-6">
              <div className="flex items-center gap-3">
                <div className="h-3 w-3 bg-pink-300 rounded-full animate-pulse"></div>
                <div className="h-3 w-3 bg-pink-400 rounded-full animate-pulse delay-100"></div>
                <div className="h-3 w-3 bg-pink-500 rounded-full animate-pulse delay-200"></div>
              </div>
            </div>
          )}
          
          {/* Connection button */}
          <div className="flex justify-center mt-4">
            {!isConnected ? (
              <KawaiiButton
                onClick={connectToLocalMcp}
                disabled={isConnecting}
                className="px-8 py-3"
              >
                <Link2 className="h-5 w-5" />
                <span>Connect to MCP Tool</span>
              </KawaiiButton>
            ) : (
              <KawaiiButton
                onClick={disconnectFromLocalMcp}
                className="px-8 py-3"
              >
                <Link2 className="h-5 w-5" />
                <span>Disconnect</span>
              </KawaiiButton>
            )}
          </div>
          
          {/* Connection info */}
          <div className="mt-6 bg-pink-50 dark:bg-pink-900/30 rounded-lg p-4 text-sm text-gray-600 dark:text-gray-400">
            <p className="font-medium text-pink-600 dark:text-pink-300 mb-2">How it works:</p>
            <ol className="list-decimal pl-5 space-y-1">
              <li>Click "Connect to MCP Tool" to open your local MCP application</li>
              <li>Your auth data will be securely passed to the app</li>
              <li>The MCP app will connect to the server using your credentials</li>
              <li>Server will link your web session with the MCP tool</li>
            </ol>
          </div>
          
          {/* Advanced settings button */}
          <div className="mt-6 flex justify-end">
            <KawaiiButton
              variant='secondary'
              onClick={() => setIsModalOpen(true)}
              className="rounded-full px-4 py-2 bg-white/50 dark:bg-gray-800/50 hover:bg-pink-200 dark:hover:bg-pink-800 text-pink-600 dark:text-pink-300 font-medium transition-colors duration-200 flex items-center gap-2"
            >
              <Wrench className="h-4 w-4" />
              <span>Advanced</span>
            </KawaiiButton>
          </div>
        </div>
        
        {/* Cute decorative element */}
        <div className="flex justify-center">
          <div className="text-pink-400 dark:text-pink-500 flex items-center gap-1">
            <Heart className="h-5 w-5 animate-pulse" />
            <span className="text-sm">MCP Connection Manager</span>
            <Heart className="h-5 w-5 animate-pulse" />
          </div>
        </div>
      </div>
      
      {/* Advanced settings modal */}
      <Modal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)}
        title="Advanced MCP Settings"
        size="md"
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-pink-600 dark:text-pink-300">
              Server WebSocket Endpoint
            </label>
            <input
              type="text"
              value={authData.serverWsEndpoint}
              onChange={(e) => setAuthData(prev => ({...prev, serverWsEndpoint: e.target.value}))}
              className="w-full rounded-full px-4 py-2 border-2 border-pink-200 dark:border-pink-700 bg-white/50 dark:bg-gray-800/50 focus:outline-hidden focus:border-pink-400 dark:focus:border-pink-500"
            />
          </div>
          
          <div className="pt-4 flex justify-end gap-2">
            <button
              onClick={() => setIsModalOpen(false)}
              className="rounded-full px-4 py-2 bg-white/50 dark:bg-gray-800/50 hover:bg-pink-100 dark:hover:bg-pink-900 text-pink-600 dark:text-pink-300 font-medium transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              onClick={() => setIsModalOpen(false)}
              className="rounded-full px-4 py-2 bg-pink-500 hover:bg-pink-600 text-white font-medium transition-colors duration-200"
            >
              Save
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export const Route = createFileRoute('/settings')({
  loader: createProtectedLoader(),
  component: MCPSettings,
});
